import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Déclaration d'utilisation de l'IA du footer : un bouton ouvre et ferme le détail.
 * S'ouvre aussi tout seul quand l'adresse se termine par #declaration-ia
 * (lien du bouton "Utilisation de l'IA" du hub).
 */
export default class AIDisclosure {
  constructor(element) {
    this.element = element;
    this.toggle = element.querySelector('.aidisclosure__toggle');
    this.panel = element.querySelector('.aidisclosure__panel');
    this.label = element.querySelector('.button-title');
    if (!this.toggle || !this.panel || !this.label) return;

    this.closedLabel = this.label.textContent.trim();
    this.openLabel = this.toggle.dataset.labelOpen || 'Masquer le détail';
    this.reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    this.isOpen = false;

    this.onToggle = this.onToggle.bind(this);
    this.onHash = this.onHash.bind(this);

    // Le détail est replié dès que le JS est prêt (visible sans JS)
    this.panel.hidden = true;
    this.element.classList.add('is-ready');

    this.toggle.addEventListener('click', this.onToggle);
    window.addEventListener('hashchange', this.onHash);

    // Arrivée depuis le hub (aboutme.html#declaration-ia)
    if (window.location.hash === '#declaration-ia') this.open();
  }

  onToggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  onHash() {
    if (window.location.hash === '#declaration-ia') this.open();
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;

    this.toggle.setAttribute('aria-expanded', 'true');
    this.label.textContent = this.openLabel;
    this.element.classList.add('is-open');
    this.panel.hidden = false;

    gsap.killTweensOf(this.panel);

    const done = () => {
      gsap.set(this.panel, { clearProps: 'height,opacity,overflow' });
      this.refresh();
      // Montre le début de la déclaration (le footer est tout en bas de la page)
      this.element.scrollIntoView({
        behavior: this.reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    };

    if (this.reduceMotion) {
      done();
      return;
    }

    gsap.fromTo(
      this.panel,
      { height: 0, opacity: 0, overflow: 'hidden' },
      {
        height: 'auto',
        opacity: 1,
        duration: 0.7,
        ease: 'power3.out',
        onComplete: done,
      },
    );
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.toggle.setAttribute('aria-expanded', 'false');
    this.label.textContent = this.closedLabel;
    this.element.classList.remove('is-open');

    gsap.killTweensOf(this.panel);

    const done = () => {
      this.panel.hidden = true;
      gsap.set(this.panel, { clearProps: 'height,opacity,overflow' });
      this.refresh();
    };

    if (this.reduceMotion) {
      done();
      return;
    }

    gsap.to(this.panel, {
      height: 0,
      opacity: 0,
      overflow: 'hidden',
      duration: 0.5,
      ease: 'power3.inOut',
      onComplete: done,
    });
  }

  // La hauteur de la page change : ScrollTrigger / ScrollSmoother doivent la remesurer
  refresh() {
    ScrollTrigger.refresh();
  }

  destroy() {
    this.toggle?.removeEventListener('click', this.onToggle);
    window.removeEventListener('hashchange', this.onHash);
    gsap.killTweensOf(this.panel);
  }
}
